"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import ErrorAlert from "@/src/components/error-alert";
import { AthenaReferralCards } from "@/src/components/tasks/athena-referral-cards";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { useProvidersForDiagnostics } from "@/src/lib/data-fetchers/diagnosis-providers-fetchers";
import { useLocationProvidersInstructions } from "@/src/lib/data-fetchers/location-providers-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { tryGetEhrSystemId } from "@/src/lib/resolve-ehr-ids";
import { getFormattedAddress } from "@/src/models/address";
import { Appointment } from "@/src/models/appointment";
import { Coverage } from "@/src/models/coverage";
import { Referral } from "@/src/models/documents/referral";
import { EHRDocument } from "@/src/models/ehr-document";
import { Patient } from "@/src/models/patient";
import { getPersonName } from "@/src/models/person";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { type LocationProvidersInstructionsEntry } from "@/src/requests/location-providers-instructions";
interface AthenaData {
  patientDetails: Patient | null;
  coverages: Coverage[];
  careTeam: Practitioner[];
  appointmentReminders: Appointment[];
  bookedAppointments: Appointment[];
  document: EHRDocument | null;
  expectedDocument: EHRDocument | null;
  documentPatientId?: string | null;
}
interface AthenaReferralInstructionsProps {
  referral: Referral;
  departmentId?: string;
  athenaData: AthenaData | null;
  customInstructions: string | null;
  showSchedulingRecommendation?: boolean;
  taskId: string;
}
type DisplayProvider = {
  text: string;
  preferred?: boolean;
};
type DisplayRow = {
  key: string;
  locationLabel: string;
  departmentName: string;
  providerGroup: string;
  providers: DisplayProvider[];
};
function buildReceivingLocationRows(params: {
  isPending: boolean;
  entries: LocationProvidersInstructionsEntry[];
  hasReceivingLocation: boolean;
  receivingLocationAddress: string;
  providersForDiagnostics: {
    name: string;
    isPreferred?: boolean;
  }[] | undefined;
}): DisplayRow[] {
  const {
    isPending,
    entries,
    hasReceivingLocation,
    receivingLocationAddress,
    providersForDiagnostics
  } = params;
  if (isPending) {
    return [];
  }
  const diagnosticProviders: DisplayProvider[] = (providersForDiagnostics ?? []).map(provider => ({
    text: provider.name,
    preferred: provider.isPreferred
  }));
  if (entries.length === 0) {
    return diagnosticProviders.length === 0 ? [] : [{
      key: "diagnosis-providers",
      locationLabel: receivingLocationAddress,
      departmentName: "N/A",
      providerGroup: "N/A",
      providers: diagnosticProviders
    }];
  }
  return entries.map(entry => ({
    key: entry.receivingLocationId,
    locationLabel: hasReceivingLocation ? receivingLocationAddress : entry.department?.name || "N/A",
    departmentName: entry.department?.name || "N/A",
    providerGroup: entry.department?.providerGroup || "N/A",
    providers: entry.providers.length > 0 ? entry.providers.map(text => ({
      text
    })) : hasReceivingLocation ? diagnosticProviders : []
  }));
}
export function AthenaReferralInstructions(t0) {
  const $ = _c(95);
  const {
    referral,
    departmentId,
    athenaData,
    customInstructions,
    showSchedulingRecommendation: t1,
    taskId
  } = t0;
  const showSchedulingRecommendation = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] !== athenaData) {
    t2 = () => {
      logger.info("Data from Athena", {
        athenaData
      });
    };
    t3 = [athenaData];
    $[0] = athenaData;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const receivingLocationId = referral.receivingLocation?.id ?? null;
  const {
    data: locationProvidersData,
    isPending
  } = useLocationProvidersInstructions(receivingLocationId);
  let t4;
  if ($[3] !== referral.diagnosisList) {
    t4 = referral.diagnosisList || [];
    $[3] = referral.diagnosisList;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data: providersForDiagnostics
  } = useProvidersForDiagnostics(t4, departmentId);
  let T0;
  let t10;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== athenaData || $[6] !== isPending || $[7] !== locationProvidersData || $[8] !== providersForDiagnostics || $[9] !== receivingLocationId || $[10] !== referral.authorizationNumber || $[11] !== referral.receivingLocation || $[12] !== referral.referringLocation || $[13] !== referral.referringProvider) {
    const receivingLocationAddress = referral.receivingLocation?.address ? getFormattedAddress(referral.receivingLocation.address) || "N/A" : "N/A";
    const receivingLocationRows = buildReceivingLocationRows({
      isPending,
      entries: locationProvidersData ?? [],
      hasReceivingLocation: receivingLocationId != null,
      receivingLocationAddress,
      providersForDiagnostics
    });
    const showCatalogBanner = receivingLocationId == null && (locationProvidersData?.length ?? 0) > 0;
    let t11;
    if ($[21] !== athenaData?.patientDetails) {
      t11 = getPersonName(athenaData?.patientDetails, true, true) || "N/A";
      $[21] = athenaData?.patientDetails;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    const patientName = t11;
    let t12;
    if ($[23] !== athenaData) {
      t12 = athenaData?.patientDetails && tryGetEhrSystemId(athenaData.patientDetails) || "N/A";
      $[23] = athenaData;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    const ehrId = t12;
    const documentId = athenaData?.document?.ehrId ?? "N/A";
    let t13;
    if ($[25] !== referral.referringProvider) {
      t13 = referral.referringProvider ? getPractitionerNameWithCredentials(referral.referringProvider) : "N/A";
      $[25] = referral.referringProvider;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    const referringProviderName = t13;
    const referringProviderNPI = referral.referringProvider?.npiNumber || "N/A";
    let t14;
    if ($[27] !== referral.referringLocation) {
      t14 = referral.referringLocation?.address ? getFormattedAddress(referral.referringLocation.address) || "N/A" : "N/A";
      $[27] = referral.referringLocation;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    const referringLocationAddress = t14;
    let t15;
    if ($[29] !== referral.authorizationNumber) {
      t15 = referral.authorizationNumber?.trim() || null;
      $[29] = referral.authorizationNumber;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    const authorizationNumber = t15;
    t9 = "space-y-8 text-foreground";
    if ($[31] !== athenaData) {
      t10 = !athenaData && <ErrorAlert compact={true} message="No Athena data available" />;
      $[31] = athenaData;
      $[32] = t10;
    } else {
      t10 = $[32];
    }
    T0 = EhrInstructionsCard;
    t5 = "text-sm text-muted-foreground";
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <h3 className="text-md font-semibold text-center mb-4">Athena Referral Instructions</h3>;
      $[33] = t6;
    } else {
      t6 = $[33];
    }
    let t16;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <span className="font-medium text-foreground">Patient Name: </span>;
      $[34] = t16;
    } else {
      t16 = $[34];
    }
    let t17;
    if ($[35] !== patientName) {
      t17 = <div>{t16}<CopyableContent text={patientName} /></div>;
      $[35] = patientName;
      $[36] = t17;
    } else {
      t17 = $[36];
    }
    let t18;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <span className="font-medium text-foreground">EHR ID: </span>;
      $[37] = t18;
    } else {
      t18 = $[37];
    }
    let t19;
    if ($[38] !== ehrId) {
      t19 = <div>{t18}<CopyableContent text={ehrId} /></div>;
      $[38] = ehrId;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    let t20;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <span className="font-medium text-foreground">Document ID: </span>;
      $[40] = t20;
    } else {
      t20 = $[40];
    }
    let t21;
    if ($[41] !== documentId) {
      t21 = <div>{t20}<CopyableContent text={documentId} /></div>;
      $[41] = documentId;
      $[42] = t21;
    } else {
      t21 = $[42];
    }
    let t22;
    if ($[43] !== athenaData) {
      t22 = athenaData?.documentPatientId && <div><span className="font-medium text-foreground">Document Patient ID: </span><CopyableContent text={athenaData.documentPatientId} /></div>;
      $[43] = athenaData;
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    const t23 = athenaData?.documentPatientId ? "" : "col-start-2";
    let t24;
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <span className="font-medium text-foreground">Referring Provider: </span>;
      $[45] = t24;
    } else {
      t24 = $[45];
    }
    let t25;
    if ($[46] !== referringProviderName) {
      t25 = <CopyableContent text={referringProviderName} />;
      $[46] = referringProviderName;
      $[47] = t25;
    } else {
      t25 = $[47];
    }
    let t26;
    if ($[48] !== t23 || $[49] !== t25) {
      t26 = <div className={t23}>{t24}{t25}</div>;
      $[48] = t23;
      $[49] = t25;
      $[50] = t26;
    } else {
      t26 = $[50];
    }
    let t27;
    if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = <span className="font-medium text-foreground">NPI: </span>;
      $[51] = t27;
    } else {
      t27 = $[51];
    }
    let t28;
    if ($[52] !== referringProviderNPI) {
      t28 = <div>{t27}<CopyableContent text={referringProviderNPI} /></div>;
      $[52] = referringProviderNPI;
      $[53] = t28;
    } else {
      t28 = $[53];
    }
    let t29;
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <span className="font-medium text-foreground">Referring Location: </span>;
      $[54] = t29;
    } else {
      t29 = $[54];
    }
    let t30;
    if ($[55] !== referringLocationAddress) {
      t30 = <div className="col-span-3">{t29}<CopyableContent text={referringLocationAddress} /></div>;
      $[55] = referringLocationAddress;
      $[56] = t30;
    } else {
      t30 = $[56];
    }
    let t31;
    if ($[57] !== authorizationNumber) {
      t31 = authorizationNumber && <div className="col-span-3"><span className="font-medium text-foreground">Authorization Number: </span><CopyableContent text={authorizationNumber} /></div>;
      $[57] = authorizationNumber;
      $[58] = t31;
    } else {
      t31 = $[58];
    }
    if ($[59] !== t17 || $[60] !== t19 || $[61] !== t21 || $[62] !== t22 || $[63] !== t26 || $[64] !== t28 || $[65] !== t30 || $[66] !== t31) {
      t7 = <div className="grid grid-cols-3 gap-y-4 mb-6">{t17}{t19}{t21}{t22}{t26}{t28}{t30}{t31}</div>;
      $[59] = t17;
      $[60] = t19;
      $[61] = t21;
      $[62] = t22;
      $[63] = t26;
      $[64] = t28;
      $[65] = t30;
      $[66] = t31;
      $[67] = t7;
    } else {
      t7 = $[67];
    }
    t8 = receivingLocationRows.length > 0 && <div className="mb-4 my-2"><h3 className="font-semibold text-foreground my-2">Receiving Location and Providers:</h3>{showCatalogBanner && <p className="text-xs text-muted-foreground mb-2">All locations — no receiving location</p>}<div className="overflow-x-auto"><table className="w-full border border-border rounded-lg"><thead className="bg-muted"><tr><th className="text-left p-3 font-medium text-foreground">Receiving Location</th><th className="text-left p-3 font-medium text-foreground">Department</th><th className="text-left p-3 font-medium text-foreground">Provider Group</th><th className="text-left p-3 font-medium text-foreground">Providers</th></tr></thead><tbody>{receivingLocationRows.map(_temp2)}</tbody></table></div></div>;
    $[5] = athenaData;
    $[6] = isPending;
    $[7] = locationProvidersData;
    $[8] = providersForDiagnostics;
    $[9] = receivingLocationId;
    $[10] = referral.authorizationNumber;
    $[11] = referral.receivingLocation;
    $[12] = referral.referringLocation;
    $[13] = referral.referringProvider;
    $[14] = T0;
    $[15] = t10;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    T0 = $[14];
    t10 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t11;
  if ($[68] !== referral || $[69] !== showSchedulingRecommendation) {
    t11 = showSchedulingRecommendation && <ReferralSchedulingRecommendation referral={referral} />;
    $[68] = referral;
    $[69] = showSchedulingRecommendation;
    $[70] = t11;
  } else {
    t11 = $[70];
  }
  let t12;
  if ($[71] !== customInstructions) {
    t12 = customInstructions && <p className="text-sm mt-4">{customInstructions}</p>;
    $[71] = customInstructions;
    $[72] = t12;
  } else {
    t12 = $[72];
  }
  let t13;
  if ($[73] !== taskId) {
    t13 = <div className="mt-4"><SyncIncompleteActions taskId={taskId} /></div>;
    $[73] = taskId;
    $[74] = t13;
  } else {
    t13 = $[74];
  }
  let t14;
  if ($[75] !== t11 || $[76] !== t12 || $[77] !== t13 || $[78] !== t5 || $[79] !== t6 || $[80] !== t7 || $[81] !== t8) {
    t14 = <div className={t5}>{t6}{t7}{t8}{t11}{t12}{t13}</div>;
    $[75] = t11;
    $[76] = t12;
    $[77] = t13;
    $[78] = t5;
    $[79] = t6;
    $[80] = t7;
    $[81] = t8;
    $[82] = t14;
  } else {
    t14 = $[82];
  }
  let t15;
  if ($[83] !== T0 || $[84] !== t14) {
    t15 = <T0>{t14}</T0>;
    $[83] = T0;
    $[84] = t14;
    $[85] = t15;
  } else {
    t15 = $[85];
  }
  let t16;
  if ($[86] !== athenaData || $[87] !== referral || $[88] !== taskId) {
    t16 = <AthenaReferralCards referral={referral} athenaData={athenaData} taskId={taskId} />;
    $[86] = athenaData;
    $[87] = referral;
    $[88] = taskId;
    $[89] = t16;
  } else {
    t16 = $[89];
  }
  let t17;
  if ($[90] !== t10 || $[91] !== t15 || $[92] !== t16 || $[93] !== t9) {
    t17 = <div className={t9}>{t10}{t15}{t16}</div>;
    $[90] = t10;
    $[91] = t15;
    $[92] = t16;
    $[93] = t9;
    $[94] = t17;
  } else {
    t17 = $[94];
  }
  return t17;
}
function _temp2(row) {
  return <tr key={row.key} className="border-t border-border"><td className="p-3 text-sm"><span className="text-muted-foreground"><CopyableContent text={row.locationLabel} /></span></td><td className="p-3 text-sm"><span className="text-muted-foreground"><CopyableContent text={row.departmentName} /></span></td><td className="p-3 text-sm"><span className="text-muted-foreground"><CopyableContent text={row.providerGroup} /></span></td><td className="p-3 text-sm"><div className="space-y-1">{row.providers.map(_temp)}</div></td></tr>;
}
function _temp(provider) {
  return <div key={provider.text}><span className={provider.preferred ? "font-semibold text-muted-foreground" : "text-muted-foreground"}><CopyableContent text={provider.text} /></span></div>;
}